"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  name: "Alex Rivera",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1533057/img/alex_rivera_portrait.png",
  feedback: "\"ViddTube\xA0has transformed how I share my content. The built-in monetization makes it easy to earn while doing what I love.\""
}, {
  id: 2,
  name: "Jamie Chen",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1533057/img/jamie_chen_portrait.png",
  feedback: "The HD streaming quality is unmatched. My viewers love the seamless playback, and I love the customizable channel features."
}, {
  id: 3,
  name: "Morgan Taylor",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1533057/img/morgan_taylor_portrait.png",
  feedback: "\"As an educator, ViddTube provides the perfect platform to reach students worldwide. The community interaction tools are fantastic.\""
}];
export default function Testimonials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const autoplayPlugin = React.useRef(Autoplay({
    delay: 5000,
    stopOnInteraction: true,
    stopOnMouseEnter: true
  }));
  React.useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  return <section id="testimonials" ref={ref} className="relative bg-background py-16 sm:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className={`text-center mb-12 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            What Creators Say
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">Hear from creators who trust ViddTube to host and monetize their content</p>
        </div>

        <div className={`relative transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <Carousel setApi={setApi} plugins={[autoplayPlugin.current]} className="w-full" opts={{
          align: "center",
          loop: true
        }}>
            <CarouselContent>
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i}>
                  <div className="p-2 sm:p-4">
                    <Card className="border border-border shadow-medium hover:shadow-strong transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]">
                      <CardContent className="flex flex-col items-center text-center p-8 sm:p-10 lg:p-12">
                        <Quote className="text-primary w-10 h-10 sm:w-12 sm:h-12 mb-6" />
                        
                        <SafeHydrate><Avatar className="w-20 h-20 sm:w-24 sm:h-24 mb-6 border-2 border-primary">
                          <AvatarImage src={testimonial.image} alt={testimonial.name} />
                          <AvatarFallback className="bg-primary text-foreground-primary text-xl font-semibold">
                            {testimonial.name.split(" ").map(n => n[0]).join("")}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>

                        <p className="text-foreground text-base sm:text-lg leading-relaxed mb-6 italic">
                          "{testimonial.feedback}"
                        </p>

                        <h3 className="text-foreground font-display font-semibold text-lg sm:text-xl">
                          {testimonial.name}
                        </h3>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            <CarouselPrevious className="hidden sm:flex -left-12 lg:-left-16 bg-surface border-border hover:bg-primary hover:text-foreground-primary hover:border-primary transition-all duration-[var(--duration-fast)]" />
            <CarouselNext className="hidden sm:flex -right-12 lg:-right-16 bg-surface border-border hover:bg-primary hover:text-foreground-primary hover:border-primary transition-all duration-[var(--duration-fast)]" />
          </Carousel>

          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => <button key={index} data-index={index} onClick={() => api?.scrollTo(index)} className={`h-2 rounded-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${current === index ? "bg-primary w-8" : "bg-muted w-2 hover:bg-primary/60"}`} aria-label={`Go to testimonial ${index + 1}`} />)}
          </div>
        </div>
      </div>
    </section>;
}